import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import Navbar from '../components/NavBar';
import Footer from '../components/Footer';
import Client from '../models/Client';
import {ClientType} from '../models/enums/ClientType';
import FollowUp from '../models/FollowUp';
import Contact from "../models/Contact";
import Interaction from "../models/Interaction";
import ErrorToast from "../components/ErrorToast";
import {getClientById} from "../services/client-service";
import EditClientModal from "../components/EditClientModal";
import DeleteClientModal from "../components/DeleteClientModal";
import {getAllContactsForClient} from "../services/contact-service";
import AddContactModal from "../components/AddContactModal";
import EditContactModal from "../components/EditContactModal";
import DeleteContactModal from "../components/DeleteContactModal";
import {getFollowUpsForClient} from "../services/follow-up-service";
import AddFollowUpModal from "../components/AddFollowUpModal";
import EditFollowUpModal from "../components/EditFollowUpModal";
import DeleteFollowUpModal from "../components/DeleteFollowUpModal";
import {getInteractionCountForClient, getRecentInteractionsForClient} from "../services/interaction-service";
import CompleteFollowUpModal from "../components/CompleteFollowUpModal";
import {useAuth} from "../context/AuthContext";
import {formatFollowUpTime} from "../utils/DateTimeUtils";
import {SubscriptionType} from "../models/enums/SubscriptionType";
import UpgradeButton from "../components/UpgradeButton";

const ClientDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const {subscriptionPlan} = useAuth();

    const [client, setClient] = useState<Client | null>(null);
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [followUps, setFollowUps] = useState<FollowUp[]>([]);
    const [interactions, setInteractions] = useState<Interaction[]>([]);
    const [totalInteractions, setTotalInteractions] = useState(0);
    const [errorToast, setErrorToast] = useState('');
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [selectedFollowUp, setSelectedFollowUp] = useState<FollowUp | null>(null);
    const [isEditClientOpen, setIsEditClientOpen] = useState(false);
    const [isDeleteClientModalOpen, setIsDeleteClientModalOpen] = useState(false);
    const [isDeleteContactModalOpen, setIsDeleteContactModalOpen] = useState(false);
    const [isAddContactModalOpen, setIsAddContactModalOpen] = useState(false);
    const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false);
    const [isAddFollowUpModalOpen, setIsAddFollowUpModalOpen] = useState(false);
    const [isEditFollowUpModalOpen, setIsEditFollowUpModalOpen] = useState(false);
    const [isDeleteFollowUpModalOpen, setIsDeleteFollowUpModalOpen] = useState(false);
    const [isCompleteFollowUpModalOpen, setIsCompleteFollowUpModalOpen] = useState(false);

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const upcomingCount = followUps.filter(fu => fu.followUpDate >= startOfToday).length;
    const overdueCount = followUps.filter(fu => fu.followUpDate < startOfToday).length;

    const disableAddFollowUp = useMemo(() => {
        return !!(subscriptionPlan?.maxFollowUps && followUps.length >= subscriptionPlan.maxFollowUps);
    }, [subscriptionPlan, followUps]);

    const disableAddContact = useMemo(() => {
        return !!(subscriptionPlan?.maxContacts && contacts.length >= subscriptionPlan.maxContacts);

    }, [subscriptionPlan, contacts]);

    useEffect(() => {
        loadClientData();
    }, [id]);

    useEffect(() => {
        const loadContacts = async () => {
            try {
                if (client) {
                    const response = await getAllContactsForClient(client.id);
                    if (response.status === 200 && response.data) {
                       setContacts(response.data)
                    } else {
                        throw new Error('Invalid contact response');
                    }
                } else {
                    throw new Error('No client id');
                }
            }  catch (err: any) {
                console.error('Error loading contacts', err);
                setErrorToast('Error loading client contacts. Please try again later.');
            }
        };
        if(client && client.type === ClientType.BUSINESS) {
            loadContacts();
        }
    }, [client]);

    useEffect(() => {
        const loadFollowUps = async () => {
            try {
                if (client) {
                    const response = await getFollowUpsForClient(client.id);
                    if (response.status === 200 && response.data) {
                        setFollowUps(response.data);
                    } else {
                        throw new Error('Invalid follow-up response');
                    }
                }  else {
                    throw new Error('No client id');
                }
            } catch (err: any) {
                console.error('Error loading follow-ups', err);
                setErrorToast('Error loading follow-ups. Please try again later.');
            }
        };
        if(client) {
            loadFollowUps();
        }
    }, [client])

    useEffect(() => {
        const loadInteractions = async () => {
            try {
                if(client) {
                    const response = await getRecentInteractionsForClient(client.id);
                    if (response.status === 200 && response.data) {
                        setInteractions(response.data);
                    } else {
                        throw new Error('Invalid interaction response');
                    }
                } else {
                    throw new Error('No client id')
                }
            } catch (err: any) {
                console.error('Error loading interactions', err);
                setErrorToast('Error loading client interactions. Please try again later.')
            }
        }
        if (client) {
            loadInteractions();
        }
    }, [client]);

    useEffect(() => {
        const getInteractionCount = async () => {
            if (client) {
                try {
                    const response = await getInteractionCountForClient(client.id);
                    if (response.data) {
                        setTotalInteractions(response.data);
                    }
                } catch(err: any) {
                    console.error('Unable to fetch interaction count');
                }
            }
        };
        getInteractionCount();
    }, [client]);

    const loadClientData = async () => {
        try {
            if (id) {
                const response = await getClientById(id);
                if (response.status === 200 && response.data) {
                    setClient(response.data)
                } else {
                    throw new Error('Invalid client response')
                }
            } else {
                throw new Error('No client id');
            }
        } catch (err: any) {
            console.error('Error loading client data', err);
            setErrorToast('Error loading client data. Please try again later.');
        }
    }

    const handleCompleteFollowUp = (id: number) => {
        setFollowUps(prev =>
            prev.filter(followUp => followUp.id !== id )
        );
        loadClientData();
    };

    const handleEditClient = (updatedClient: Client) => {
        setClient(updatedClient);
    }

    const handleEditContact = (updatedContact: Contact) => {
        setContacts(prev =>
            prev.map(contact => (contact.id === updatedContact.id ? updatedContact : contact))
        );
    }

    const handleDeleteContact = (id: number) => {
        setContacts(prev =>
            prev.filter(contact => contact.id !== id)
        );
    }

    const handleAddFollowUp = (newFollowUp: FollowUp) => {
        setFollowUps(prev => {
            const updated = [...prev, newFollowUp];
            updated.sort((a, b) =>
                new Date(a.followUpDate).getTime() - new Date(b.followUpDate).getTime()
            );
            return updated;
        });
    };

    const handleAddContact = (newContact: Contact) => {
        setContacts(prev => {
            const updated = [...prev, newContact];
            return updated.sort((a, b) => a.name.localeCompare(b.name));
        });
    };

    const handleUpdateFollowUp = (updatedFollowUp: FollowUp) => {
        setFollowUps(prev => {
            const updatedList = prev.map(followUp =>
                followUp.id === updatedFollowUp.id ? updatedFollowUp : followUp
            );
            updatedList.sort((a, b) => new Date(a.followUpDate).getTime() - new Date(b.followUpDate).getTime());
            return updatedList;
        });
    };

    const handleDeleteFollowUp = (id: number) => {
        setFollowUps(prev => prev.filter(followUp => followUp.id !== id));
    }

        return (
        client === null ?
            <div className="min-h-screen flex flex-col">
                <Navbar />
                <main className="flex-grow container mx-auto px-4 py-6 text-center">Loading...</main>
                <Footer />
                <ErrorToast data-testid="error-toast" message={errorToast} clearError={() => setErrorToast('')} />
            </div>
            :
            <div className="min-h-screen flex flex-col">
                <Navbar />
                <main className="flex-grow container mx-auto px-4 py-6">
                    <button
                        onClick={() => navigate('/clients')}
                        className="text-crumbblue hover:underline mb-4"
                    >
                        &larr; Back to Clients
                    </button>
                    {subscriptionPlan?.subscriptionType === SubscriptionType.FREE && (
                        <div className="flex justify-end mb-4" data-testid="top-upgrade">
                            <UpgradeButton />
                        </div>
                    )}

                    <div data-testid="client-details" className="mb-6">
                        <h1 className="text-3xl font-bold mb-2">{client.name}</h1>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <p><strong>Type:</strong> {client.type}</p>
                                <p><strong>Status:</strong> {client.status}</p>
                                {client.created && (
                                    <p><strong>Client Since:</strong> {client.created.toLocaleDateString()}</p>
                                )}
                            </div>
                            <div>
                                {client.lastActivity && (
                                    <p><strong>Last Activity:</strong> {client.lastActivity}</p>
                                )}
                                <p><strong>Last Activity Date:</strong> {client.lastActivityDate.toLocaleDateString()}</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                {client.phone && <p><strong>Phone:</strong> {client.phone}</p>}
                                {client.email && <p><strong>Email:</strong> {client.email}</p>}
                                {client.preferredContact && <p><strong>Preferred Contact:</strong> {client.preferredContact}</p>}
                            </div>
                            <div>
                                {client.tags.length > 0 && (
                                    <p><strong>Tags:</strong> {client.tags.join(', ')}</p>
                                )}
                                {client.notes && <p><strong>Notes:</strong> {client.notes}</p>}
                            </div>
                        </div>
                        <div className="flex items-center space-x-4 mt-6">
                            <button
                                data-testid="edit-client"
                                onClick={() => setIsEditClientOpen(true)}
                                className="px-5 py-2 rounded bg-crumbblue text-white font-medium shadow hover:shadow-lg transition"
                            >
                                Edit Client
                            </button>
                            <button
                                data-testid="view-all-interactions-top"
                                onClick={() => navigate(`/client/history/${client.id}`)}
                                className="px-5 py-2 rounded bg-white text-crumbblue font-medium border border-crumbblue shadow-sm hover:bg-crumbblue/10 transition"
                            >
                                View Client History
                            </button>
                            <button
                                data-testid="delete-client"
                                className="px-5 py-2 rounded bg-red-600 text-white font-medium shadow-sm hover:bg-red-700 transition"
                                onClick={() => setIsDeleteClientModalOpen(true)}
                            >
                                Delete Client
                            </button>
                        </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                        <div className="p-4 bg-white border rounded shadow" data-testid="upcoming-follow-ups">
                            <h2 className="text-lg font-semibold">Upcoming Follow-Ups</h2>
                            <p className="text-2xl">{upcomingCount}</p>
                        </div>
                        <div className="p-4 bg-white border rounded shadow" data-testid="overdue-follow-ups">
                            <h2 className="text-lg font-semibold">Overdue Follow-Ups</h2>
                            <p className="text-2xl">{overdueCount}</p>
                        </div>
                        <div className="p-4 bg-white border rounded shadow" data-testid="total-interactions">
                            <h2 className="text-lg font-semibold">Total Interactions</h2>
                            <p className="text-2xl">{totalInteractions}</p>
                        </div>
                    </div>

                    {/* Contacts (Business Only) */}
                    {client.type === ClientType.BUSINESS && (
                        <section className="mb-8" data-testid="contacts-section">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-2xl font-semibold">Contacts</h2>
                                <div className="flex items-center gap-3" data-testid="contact-actions">
                                    {disableAddContact && subscriptionPlan?.subscriptionType === SubscriptionType.FREE && (
                                        <UpgradeButton />
                                    )}
                                    <button
                                        data-testid="add-contact"
                                        className={`px-4 py-2 rounded ${
                                            disableAddContact
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed hover:bg-gray-300'
                                                : 'bg-crumborange text-white hover:bg-orange-600'
                                        }`}
                                        onClick={() => setIsAddContactModalOpen(true)}
                                        disabled={disableAddContact}
                                    >
                                        + New Contact
                                    </button>
                                </div>
                            </div>
                            {disableAddContact && subscriptionPlan?.maxContacts != null && (
                                <div className="mb-3 text-sm text-gray-600" data-testid="contact-limit-message">
                                    You’ve reached the maximum of <span className="font-semibold">{subscriptionPlan.maxContacts}</span> contacts for your plan.
                                    <span className="ml-1">Upgrade your plan to add more.</span>
                                </div>
                            )}
                            {contacts.length > 0 ? (
                                <table data-testid="contacts-table" className="w-full bg-white border rounded">
                                    <thead>
                                    <tr className="bg-gray-100 text-left">
                                        <th className="px-4 py-2">Name</th>
                                        <th className="px-4 py-2">Preferred Contact</th>
                                        <th className="px-4 py-2">Email</th>
                                        <th className="px-4 py-2">Phone</th>
                                        <th className="px-4 py-2">Notes</th>
                                        <th className="px-4 py-2">Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {contacts.map(contact => (
                                        <tr key={contact.id}
                                            className="border-t hover:bg-gray-50"
                                            data-testid={"contact-" + contact.id}
                                        >
                                            <td className="px-4 py-2">{contact.name}</td>
                                            <td className="px-4 py-2">{contact.preferredContact}</td>
                                            <td className="px-4 py-2">{contact.email}</td>
                                            <td className="px-4 py-2">{contact.phone}</td>
                                            <td className="px-4 py-2">{contact.notes}</td>
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <div className="flex space-x-2">
                                                    <button
                                                        data-testid={"edit-contact-" + contact.id}
                                                        className="text-crumbblue hover:underline"
                                                        onClick={() => {
                                                            setSelectedContact(contact);
                                                            setIsEditContactModalOpen(true);
                                                        }}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="text-red-500 hover:underline"
                                                        data-testid={"delete-contact-" + contact.id}
                                                        onClick={() => {
                                                            setSelectedContact(contact);
                                                            setIsDeleteContactModalOpen(true);
                                                        }}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="text-center text-gray-500 py-6 border rounded">
                                    You haven't added any contacts yet. Click "New Contact" to get started!
                                </div>
                            )}
                        </section>
                    )}

                    {/* Follow-Ups */}
                    <section className="mb-8" data-testid="follow-ups-section">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-semibold">Follow-Ups</h2>
                            <div className="flex items-center gap-3" data-testid="follow-up-actions">
                                {disableAddFollowUp && subscriptionPlan?.subscriptionType === SubscriptionType.FREE && (
                                    <UpgradeButton />
                                )}
                                <button
                                    data-testid="add-follow-up"
                                    className={`px-4 py-2 rounded ${
                                        disableAddFollowUp
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed hover:bg-gray-300' 
                                            : 'bg-crumborange text-white hover:bg-orange-600'
                                    }`}
                                    onClick={() => setIsAddFollowUpModalOpen(true)}
                                    disabled={disableAddFollowUp}
                                >
                                    + New Follow-Up
                                </button>
                            </div>
                        </div>
                        {disableAddFollowUp && subscriptionPlan?.maxFollowUps != null && (
                            <div className="mb-3 text-sm text-gray-600" data-testid="follow-up-limit-message">
                                You’ve reached the maximum of <span className="font-semibold">{subscriptionPlan.maxFollowUps}</span> follow-ups for your plan.
                                <span className="ml-1">Upgrade your plan to add more.</span>
                            </div>
                        )}
                        {followUps.length > 0 ? (
                            <table className="w-full bg-white border rounded" data-testid="follow-up-table">
                                <thead>
                                <tr className="bg-gray-100 text-left">
                                    <th className="px-4 py-2">Complete</th>
                                    <th className="px-4 py-2">Date</th>
                                    <th className="px-4 py-2">Time</th>
                                    <th className="px-4 py-2">Preferred Contact</th>
                                    <th className="px-4 py-2">Notes</th>
                                    <th className="px-4 py-2">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {followUps.map(followUp => {
                                    const now = new Date();
                                    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                    const startOfTomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);

                                    const isOverdue  = followUp.followUpDate < startOfToday;
                                    const isDueToday = followUp.followUpDate >= startOfToday && followUp.followUpDate < startOfTomorrow;
                                    const timeDisplay = formatFollowUpTime(followUp);
                                    return (
                                        <tr
                                            key={followUp.id}
                                            data-testid={"follow-up-" + followUp.id}
                                            className={`border-t hover:bg-gray-50 ${
                                                isOverdue ? 'bg-red-50'
                                                    : isDueToday ? 'bg-yellow-50'
                                                        : ''
                                            }`}
                                        >
                                            <td className="px-4 py-2">
                                                <button
                                                    data-testid={`complete-follow-up-button-${followUp.id}`}
                                                    className="px-3 py-1 bg-crumbblue text-white text-sm font-medium rounded hover:bg-crumbblue/90 transition"
                                                    onClick={() => {
                                                        setSelectedFollowUp(followUp);
                                                        setIsCompleteFollowUpModalOpen(true);
                                                    }}
                                                    aria-label={`Complete follow-up scheduled for ${followUp.followUpDate.toLocaleDateString()}`}
                                                >
                                                    Complete
                                                </button>
                                            </td>
                                            <td className="px-4 py-2 align-middle">
                                                <div className="inline-flex items-center">
                                                    {followUp.followUpDate.toLocaleDateString()}
                                                    {isOverdue && (
                                                        <span data-testid="overdue" className="ml-2" title="Overdue">⚠️</span>
                                                    )}
                                                    {isDueToday && (
                                                        <span data-testid="due-today" className="ml-2" title="Due today">📅</span>
                                                    )}
                                                </div>
                                             </td>
                                            <td
                                                className="px-4 py-2 align-middle text-gray-700"
                                                data-testid={`follow-up-time-${followUp.id}`}
                                                aria-label="Follow-up time"
                                            >
                                                {timeDisplay ?? '—'}
                                            </td>
                                            <td className="px-4 py-2">{followUp.contactOption}</td>
                                            <td className="px-4 py-2">{followUp.notes}</td>
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <div className="flex space-x-2">
                                                    <button
                                                        className="text-crumbblue hover:underline mr-2"
                                                        data-testid={"edit-follow-up-" + followUp.id}
                                                        onClick={() => {
                                                            setSelectedFollowUp(followUp);
                                                            setIsEditFollowUpModalOpen(true);
                                                        }}
                                                    >
                                                        Edit</button>
                                                    <button
                                                        className="text-red-500 hover:underline"
                                                        data-testid={"delete-follow-up-" + followUp.id}
                                                        onClick={() => {
                                                            setSelectedFollowUp(followUp);
                                                            setIsDeleteFollowUpModalOpen(true);
                                                        }}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                            ): (
                            <div className="text-center text-gray-500 py-6 border rounded">
                                There are no active follow-ups. Click "New Follow-Up" to create one!
                            </div>
                            )}

                    </section>

                    {/* Interactions */}
                    <section data-testid="interactions-section">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-semibold">Recent Interactions</h2>
                            <div className="flex space-x-2">
                                <button
                                    data-testid="view-all-interactions"
                                    onClick={() => navigate(`/client/history/${client.id}`)}
                                    className="text-crumbblue hover:underline"
                                >
                                    View All
                                </button>
                                <button
                                    data-testid="add-interaction"
                                    onClick={() => navigate(`/interaction/new/${client.id}`)}
                                    className="bg-crumborange text-white px-4 py-2 rounded hover:bg-orange-600"
                                >
                                    + New Interaction
                                </button>
                            </div>
                        </div>

                        {interactions.length > 0 ? (
                            <ul data-testid="interactions-list">
                                {interactions.map((interaction) => (
                                    <li
                                        key={interaction.id}
                                        data-testid={`interaction-${interaction.id}`}
                                        className="mb-4 p-4 bg-white border rounded shadow cursor-pointer hover:bg-gray-50"
                                        onClick={() => navigate(`/interaction/${interaction.id}`)}
                                    >
                                        <p className="font-semibold">{interaction.subject}</p>
                                        <p className="text-sm text-gray-500">
                                            {interaction.interactionDate.toLocaleDateString()} &middot; {interaction.type}
                                        </p>
                                        <p className="mt-1">{interaction.outcome}</p>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="text-center text-gray-500 py-6 border rounded">
                                There are no client interactions. Click “New Interaction” to create one!
                            </div>
                        )}
                    </section>

                </main>
                <Footer />
                <EditClientModal
                    isOpen={isEditClientOpen}
                    onClose={() => setIsEditClientOpen(false)}
                    onError={(error) => setErrorToast(error)}
                    onEditClient={(client) => handleEditClient(client)}
                    client={client}
                />
                <DeleteClientModal
                    isOpen={isDeleteClientModalOpen}
                    clientId={client.id}
                    onClose={() => setIsDeleteClientModalOpen(false)}
                    onDelete={() => navigate('/clients')}
                    onError={error => setErrorToast(error)}
                />
                {selectedContact && selectedContact.id && (
                    <DeleteContactModal
                        isOpen={isDeleteContactModalOpen}
                        contactId={selectedContact.id}
                        onClose={() => {
                            setIsDeleteContactModalOpen(false);
                            setSelectedContact(null);
                        }}
                        onDelete={(id) => handleDeleteContact(id)}
                        onError={(error) => setErrorToast(error)}
                        />
                    )}
                <AddContactModal
                    isOpen={isAddContactModalOpen}
                    onClose={() => setIsAddContactModalOpen(false)}
                    onError={(error: string) => setErrorToast(error)}
                    onAddContact={(contact: Contact) => handleAddContact(contact)}
                    clientId={client.id}
                    />
                {selectedContact && (
                    <EditContactModal
                        isOpen={isEditContactModalOpen}
                        onClose={() => {
                            setIsEditContactModalOpen(false);
                            setSelectedContact(null);
                        }}
                        onError={(error: string) => setErrorToast(error)}
                        onEditContact={(contact: Contact) => handleEditContact(contact)}
                        contact={selectedContact}
                        />
                    )
                }
                <AddFollowUpModal
                    isOpen={isAddFollowUpModalOpen}
                    onClose={() => setIsAddFollowUpModalOpen(false)}
                    onError={(error) => setErrorToast(error)}
                    onFollowUpAdded={(followUp) => handleAddFollowUp(followUp)}
                    clientId={client.id} />
                {selectedFollowUp && (
                    <EditFollowUpModal
                        isOpen={isEditFollowUpModalOpen}
                        onClose={() => {
                            setIsEditFollowUpModalOpen(false);
                            setSelectedFollowUp(null);
                        }}
                        onError={(error) => setErrorToast(error)}
                        onUpdateFollowUp={(followUp) => handleUpdateFollowUp(followUp)}
                        followUp={selectedFollowUp} />
                    )
                }
                {selectedFollowUp && selectedFollowUp.id && (
                        <DeleteFollowUpModal
                            isOpen={isDeleteFollowUpModalOpen}
                            followUpId={selectedFollowUp.id}
                            onClose={() => {
                                setIsDeleteFollowUpModalOpen(false);
                                setSelectedFollowUp(null);
                            }}
                            onDelete={(id) => handleDeleteFollowUp(id)}
                            onError={(error) => setErrorToast(error)} />
                    )
                }
                {selectedFollowUp && (
                    <CompleteFollowUpModal
                        isOpen={isCompleteFollowUpModalOpen}
                        onClose={() => {
                            setIsCompleteFollowUpModalOpen(false);
                            setSelectedFollowUp(null);
                        }}
                        onError={(error) => setErrorToast(error)}
                        onComplete={(followUpId) => handleCompleteFollowUp(followUpId)}
                        followUp={selectedFollowUp}
                        clientId={client.id} />
                )}
                <ErrorToast data-testid="error-toast" message={errorToast} clearError={() => setErrorToast('')} />
            </div>
    );
};

export default ClientDetailsPage;